import { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isAfter, isBefore, startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Clock, Cpu } from 'lucide-react';
import './Calendar.css';

function Calendar({ user }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [bookings, setBookings] = useState([]);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

    // Generate time slots (24 hours)
    const timeSlots = Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        label: `${i.toString().padStart(2, '0')}:00`
    }));

    useEffect(() => {
        // TODO: Fetch bookings from API
        // Simulated bookings for demo
        setBookings([
            {
                id: '1',
                userId: user.email,
                date: new Date(),
                startHour: 10,
                endHour: 12,
                gpuCount: 1,
                status: 'active'
            },
            {
                id: '2',
                userId: 'other@neuais.com',
                date: new Date(),
                startHour: 14,
                endHour: 16,
                gpuCount: 1,
                status: 'active'
            }
        ]);
    }, [user]);

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const handlePreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
    const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

    const handleDateClick = (date) => {
        setSelectedDate(date);
    };

    const getBookingsForDate = (date) => {
        return bookings.filter(booking =>
            isSameDay(new Date(booking.date), date)
        );
    };

    const isSlotAvailable = (hour) => {
        const dayBookings = getBookingsForDate(selectedDate);
        return !dayBookings.some(booking =>
            hour >= booking.startHour && hour < booking.endHour
        );
    };

    const handleTimeSlotClick = (hour) => {
        if (isSlotAvailable(hour)) {
            setSelectedTimeSlot(hour);
            setShowBookingModal(true);
        }
    };

    const handleBooking = async (duration) => {
        // TODO: API call to create booking
        const newBooking = {
            id: Date.now().toString(),
            userId: user.email,
            date: selectedDate,
            startHour: selectedTimeSlot,
            endHour: selectedTimeSlot + duration,
            gpuCount: 1,
            status: 'active'
        };

        setBookings([...bookings, newBooking]);
        setShowBookingModal(false);
        setSelectedTimeSlot(null);
    };

    return (
        <div className="calendar-page">
            <div className="container">
                <div className="calendar-header">
                    <div>
                        <h1>GPU Time Scheduler</h1>
                        <p>Book your GPU time slots and manage reservations</p>
                    </div>
                    <div className="gpu-status glass-card">
                        <Cpu size={24} className="gpu-icon" />
                        <div>
                            <div className="gpu-label">Available GPUs</div>
                            <div className="gpu-count">4x A100</div>
                        </div>
                    </div>
                </div>

                <div className="calendar-grid">
                    {/* Month Calendar */}
                    <div className="month-calendar glass-card">
                        <div className="month-header">
                            <button onClick={handlePreviousMonth} className="btn-icon">
                                <ChevronLeft />
                            </button>
                            <h3>{format(currentDate, 'MMMM yyyy')}</h3>
                            <button onClick={handleNextMonth} className="btn-icon">
                                <ChevronRight />
                            </button>
                        </div>

                        <div className="weekdays">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                <div key={day} className="weekday">{day}</div>
                            ))}
                        </div>

                        <div className="days-grid">
                            {daysInMonth.map((date, index) => {
                                const dateBookings = getBookingsForDate(date);
                                const isToday = isSameDay(date, new Date());
                                const isSelected = isSameDay(date, selectedDate);
                                const isPast = isBefore(startOfDay(date), startOfDay(new Date()));

                                return (
                                    <div
                                        key={index}
                                        className={`day-cell ${!isSameMonth(date, currentDate) ? 'other-month' : ''} 
                      ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isPast ? 'past' : ''}`}
                                        onClick={() => !isPast && handleDateClick(date)}
                                    >
                                        <div className="day-number">{format(date, 'd')}</div>
                                        {dateBookings.length > 0 && (
                                            <div className="booking-indicator">
                                                {dateBookings.length} booking{dateBookings.length > 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Time Slots */}
                    <div className="time-slots glass-card">
                        <div className="time-slots-header">
                            <h3>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</h3>
                            <p>Click on an available slot to book</p>
                        </div>

                        <div className="slots-grid">
                            {timeSlots.map(({ hour, label }) => {
                                const available = isSlotAvailable(hour);
                                const booking = getBookingsForDate(selectedDate).find(
                                    b => hour >= b.startHour && hour < b.endHour
                                );

                                return (
                                    <div
                                        key={hour}
                                        className={`time-slot ${available ? 'available' : 'booked'} 
                      ${booking?.userId === user.email ? 'my-booking' : ''}`}
                                        onClick={() => available && handleTimeSlotClick(hour)}
                                    >
                                        <Clock size={16} />
                                        <span className="slot-time">{label}</span>
                                        {booking && (
                                            <span className="slot-user">
                                                {booking.userId === user.email ? 'You' : 'Booked'}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Booking Modal */}
            {showBookingModal && (
                <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
                    <div className="modal-content glass-card" onClick={(e) => e.stopPropagation()}>
                        <h3>Book GPU Time</h3>
                        <p>Starting at {selectedTimeSlot}:00 on {format(selectedDate, 'MMM d, yyyy')}</p>

                        <div className="booking-options">
                            <button onClick={() => handleBooking(1)} className="btn btn-secondary">
                                1 Hour
                            </button>
                            <button onClick={() => handleBooking(2)} className="btn btn-secondary">
                                2 Hours
                            </button>
                            <button onClick={() => handleBooking(4)} className="btn btn-primary">
                                4 Hours
                            </button>
                        </div>

                        <button onClick={() => setShowBookingModal(false)} className="btn btn-secondary">
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Calendar;
